import {
  FINTRAC_STR_BENEFICIARY_TYPE_CODE_OPTIONS,
  FINTRAC_STR_DEFINITION_TYPE_CODE_MAP,
} from 'app/modules/fintracStr/formConstants';
import {
  getDependentPaths,
  getUniqueDefinitionOptions,
} from 'app/modules/fintracStr/formUtils';

import { FintracFormSingleField } from 'app/modules/fintracStr/models';
import { isFalse, isFieldBlank } from 'app/modules/fintracStr/renderConditions';
import {
  U21FormFieldTypes,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import { isUndefined } from 'lodash';

export const FINTRAC_STR_TRANSACTION_COMPLETING_ACTIONS_BENEFICIARIES_FIELDS: FintracFormSingleField =
  {
    type: 'section',
    repeatable: true,
    label: 'Beneficiaries',
    name: 'beneficiaries',
    mustExistInPayload: true,
    mustHaveValueIfExist: false,
    dependentPaths: (sectionIndex, fieldSectionIndex) =>
      getDependentPaths(
        undefined,
        fieldSectionIndex && fieldSectionIndex.length === 1
          ? [
              `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.beneficiaryIndicator`,
            ]
          : undefined,
        sectionIndex,
        fieldSectionIndex,
      ),
    hideIfTrue: {
      and: [
        {
          path: (sectionIndex, fieldSectionIndex) =>
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.beneficiaryIndicator`
              : '',
          conditions: (value) => isFalse(value),
        },
      ],
    },
    fields: [
      {
        type: U21FormFieldTypes.SELECT,
        name: 'typeCode',
        label: 'Account type',
        fieldProps: (_, mutators, sectionIndex, fieldSectionIndex) =>
          fieldSectionIndex && fieldSectionIndex.length === 2
            ? {
                options: FINTRAC_STR_BENEFICIARY_TYPE_CODE_OPTIONS,
                onChange: () => {
                  mutators.setFieldValueUntyped(
                    `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries[${fieldSectionIndex[1]}].refId`,
                    '',
                  );
                },
              }
            : { options: FINTRAC_STR_BENEFICIARY_TYPE_CODE_OPTIONS },
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.SELECT,
        name: 'refId',
        label: 'Subject name',
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 2
              ? [
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries[${fieldSectionIndex[1]}].typeCode`,
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries`,
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries[${fieldSectionIndex[1]}]`,
                  'definitions', // Need to listen to definitions since getUniqueDefinitionOptions relies on definitions
                ]
              : ['definitions'],
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 2
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries[${fieldSectionIndex[1]}].typeCode`
                  : '',
              conditions: (value) => isFieldBlank(value),
            },
          ],
        },
        fieldProps: (
          flattenedValues,
          mutators,
          sectionIndex,
          fieldSectionIndex,
        ) => ({
          noOptionsText: ((): string => {
            if (
              !isUndefined(sectionIndex) &&
              fieldSectionIndex &&
              fieldSectionIndex.length === 2
            ) {
              const label =
                FINTRAC_STR_DEFINITION_TYPE_CODE_MAP[
                  flattenedValues[
                    `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries[${fieldSectionIndex[1]}].typeCode`
                  ]
                ];

              if (label) {
                return `No options available. Please create a new "${label}" definition in the definitions tab.`;
              }
            }

            return `No options available. Please create a new definition in the definitions tab.`;
          })(),
          options: ((): U21SelectOptionProps<string>[] =>
            !isUndefined(sectionIndex) &&
            fieldSectionIndex &&
            fieldSectionIndex.length === 2
              ? getUniqueDefinitionOptions(
                  flattenedValues,
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries`,
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries[${fieldSectionIndex[1]}]`,
                )
              : [])(),
          onChange: (value: string) => {
            if (
              !isUndefined(sectionIndex) &&
              fieldSectionIndex &&
              fieldSectionIndex.length === 2
            ) {
              const definition = (flattenedValues.definitions ?? []).find(
                (def: { refId: string }) => def.refId === value,
              );
              const autofilledFields = definition?.autofilledFields?.[0];
              const beneficiaryPath = `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].beneficiaries[${fieldSectionIndex[1]}]`;

              const existingValues = flattenedValues[beneficiaryPath]?.details;

              if (!existingValues?.clientNumber) {
                mutators.setFieldValueUntyped(
                  `${beneficiaryPath}.details.clientNumber`,
                  autofilledFields?.clientNumber ?? '',
                );
              }

              if (!existingValues?.username) {
                mutators.setFieldValueUntyped(
                  `${beneficiaryPath}.details.username`,
                  autofilledFields?.username ?? '',
                );
              }

              if (!existingValues?.emailAddress) {
                mutators.setFieldValueUntyped(
                  `${beneficiaryPath}.details.emailAddress`,
                  autofilledFields?.emailAddress ?? '',
                );
              }
            }
          },
        }),
      },
      {
        type: 'section',
        repeatable: false,
        label: 'Details',
        name: 'details',
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
        isSectionWrapperHidden: true,
        fields: [
          [
            {
              type: U21FormFieldTypes.TEXT,
              name: 'clientNumber',
              label: 'Client number',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
            },
            {
              type: U21FormFieldTypes.TEXT,
              name: 'username',
              label: 'Username',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
            },
          ],
          {
            type: U21FormFieldTypes.TEXT,
            name: 'emailAddress',
            label: 'Email address',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
          },
        ],
      },
    ],
  };
